import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "uppercase" }

import DataTableActions from '@/components/datatable/DataTableActions.vue';
    import { Payslip } from '@/types';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import { computed, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { CircleEllipsis } from 'lucide-vue-next';
    import {
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuTrigger,
    } from '@/components/ui/dropdown-menu';
    import { Button } from '@/components/ui/button';
    import { popupCenterDual } from '@/utils/helpers';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import axios from 'axios';
    import PayslipUpload from './PayslipUpload.vue';
    import PayslipSendReminder from './PayslipSendReminder.vue';
    import PayslipDelete from './PayslipDelete.vue';
    import PayslipApprove from './PayslipApprove.vue';
    import PayslipReject from './PayslipReject.vue';
    import { toast } from 'vue-sonner';
    import AddButton from '@/components/AddButton.vue';
    import QueryKey from '@/enums/QueryKey';
    import { useQueryClient } from '@tanstack/vue-query';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'PayslipTableActions',
  props: {
    payslip: {}
  },
  setup(__props: any) {

    const props = __props;

    const page = usePage<SharedData>();
    const user = computed(() => page.props.auth.user);

    const uploadModalOpen = ref(false);
    const sendReminderModalOpen = ref(false);
    const deleteModalOpen = ref(false);
    const approveModalOpen = ref(false);
    const rejectModalOpen = ref(false);

    const queryClient = useQueryClient();
    const { t } = useI18n();

    const isFinalWithoutCustomer = computed(() => {
        return props.payslip.status === 'upload-final-without-customer';
    });

    const isApprovedWithoutCustomer = computed(() => {
        return (
            props.payslip.status !== 'approved' &&
            props.payslip.status !== 'upload-final' &&
            props.payslip.sent_to_payroll_system_at
        );
    });

    const isRejected = computed(() => {
        return props.payslip.status === 'rejected';
    });

    const isApproved = computed(() => {
        return props.payslip.status === 'approved';
    });

    const isUploaded = computed(() => {
        return props.payslip.status === 'uploaded';
    });

    const isFinal = computed(() => {
        return props.payslip.status === 'upload-final';
    });

    const canUpdatePayslips = computed(() => {
        return !user.value.is_client && hasPermission(Permission.SALARY_PAYSLIPS_UPDATE);
    });

    const isFirstApprover = computed(() => {
        return user.value.id == props.payslip.company.salary_approval_steps?.first_approver_id;
    });

    const isSecondApprover = computed(() => {
        return user.value.id == props.payslip.company.salary_approval_steps?.next_approver_id;
    });

    const isApprovable = computed(() => {
        if (!isUploaded.value || !user.value.is_client) {
            return false;
        }
        if (
            props.payslip.company.salary_two_step_approval &&
            props.payslip.read_by_first_approver &&
            isFirstApprover
        ) {
            return true;
        } else if (
            props.payslip.company.salary_two_step_approval &&
            props.payslip.read_by_next_approver &&
            isSecondApprover.value
        ) {
            return true;
        } else if (
            !props.payslip.company.salary_two_step_approval &&
            props.payslip.read_by_client
        ) {
            return true;
        }

        return false;
    });

    const hasSomeStatus = computed(() => {
        return (
            isFinalWithoutCustomer.value ||
            isApprovedWithoutCustomer.value ||
            isRejected.value ||
            isApproved.value ||
            isUploaded.value ||
            isFinal.value
        );
    });

    const text = computed(() => {
        if (isFinalWithoutCustomer.value) {
            return t('forms.upload-final-without-customer');
        } else if (isApprovedWithoutCustomer.value) {
            return t('forms.approved-without-customer');
        } else if (isRejected.value) {
            return t('forms.rejected');
        } else if (isApproved.value) {
            return t('forms.approved');
        } else if (isUploaded.value) {
            if (!user.value.is_client) {
                return t('forms.uploaded');
            } else {
                if (props.payslip.company.salary_two_step_approval) {
                    if (
                        (!props.payslip.user && isFirstApprover.value) ||
                        (props.payslip.user && props.payslip.user.id !== user.value.id)
                    ) {
                        return t('pay-slips.awaiting_approval');
                    } else {
                        return t('pay-slips.awaiting_other_approver');
                    }
                } else {
                    return t('pay-slips.awaiting_approval');
                }
            }
        } else if (isFinal.value) {
            return t('forms.upload-final');
        }

        return t('forms.not_uploaded');
    });

    const color = computed(() => {
        if (isFinalWithoutCustomer.value || isFinal.value || isUploaded.value) {
            return 'text-primary';
        } else if (isApprovedWithoutCustomer.value || isApproved.value) {
            return 'text-warning';
        } else if (isRejected.value) {
            return 'text-destructive';
        }

        return 'text-destructive';
    });

    const { hasPermission } = usePermissions();

    const openFile = (url: string, name?: string) => {
        if (!props.payslip.read_by_client && user.value.is_client) {
            markAsRead();
        }

        popupCenterDual(url, name || 'preview', window.innerWidth / 2, window.innerHeight / 1.1);
    };

    const openFileWithApprovers = (url: string, name?: string) => {
        if (
            props.payslip.company.salary_two_step_approval &&
            user.value.is_client &&
            !props.payslip.read_by_first_approver &&
            isFirstApprover.value
        ) {
            markAsReadByFirstApprover();
        } else if (
            props.payslip.company.salary_two_step_approval &&
            user.value.is_client &&
            !props.payslip.read_by_next_approver &&
            isSecondApprover.value
        ) {
            markAsReadBySecondApprover();
        }

        if (!props.payslip.read_by_client && user.value.is_client) {
            markAsRead();
        }

        popupCenterDual(url, name || 'preview', window.innerWidth / 2, window.innerHeight / 1.1);
    };

    const markAsReadByFirstApprover = () => {
        axios
            .post(route('v2.salary.payslip.mark-as-read-by-first-approver'), {
                id: props.payslip.id,
            })
            .then(() => {
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLLS, user.value.selected_company_id],
                });
            });
    };

    const markAsReadBySecondApprover = () => {
        axios
            .post(route('v2.salary.payslip.mark-as-read-by-next-approver'), {
                id: props.payslip.id,
            })
            .then(() => {
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLLS, user.value.selected_company_id],
                });
            });
    };

    const markAsRead = () => {
        axios
            .post(route('v2.salary.payslip.mark-as-read'), {
                id: props.payslip.id,
            })
            .then(() => {
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLLS, user.value.selected_company_id],
                });
            });
    };

    const openUploadDialog = () => {
        uploadModalOpen.value = true;
    };

    const openSendReminderDialog = () => {
        sendReminderModalOpen.value = true;
    };

    const openDeleteDialog = () => {
        deleteModalOpen.value = true;
    };

    const openApproveDialog = () => {
        if (isSecondApprover.value && !props.payslip.approved_by) {
            toast.error(t('salary.pay_slips.two_step_approval.cannot_approve'));
            return;
        }
        approveModalOpen.value = true;
    };

    const openRejectDialog = () => {
        if (isSecondApprover.value && !props.payslip.approved_by) {
            toast.error(t('salary.pay_slips.two_step_approval.cannot_approve'));
            return;
        }
        rejectModalOpen.value = true;
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DataTableActions, { data: _ctx.payslip }, {
    default: _withCtx(() => [
      (hasSomeStatus.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["flex items-center gap-1", color.value])
          }, [
            _createElementVNode("span", _hoisted_1, _toDisplayString(text.value), 1),
            _createVNode(_unref(DropdownMenu), null, {
              default: _withCtx(() => [
                _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Button), {
                      variant: "inherit",
                      size: "xs",
                      width: "narrow"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(CircleEllipsis))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DropdownMenuContent), null, {
                  default: _withCtx(() => [
                    (isUploaded.value)
                      ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                          key: 0,
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (
                            openFileWithApprovers(
                                _ctx.route('v2.salary.payslip.get', { paySlipId: _ctx.payslip.id }),
                            )
                        ))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('pay-slips.open')), 1)
                          ]),
                          _: 1
                        }))
                      : (hasSomeStatus.value)
                        ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                            key: 1,
                            onClick: _cache[1] || (_cache[1] = ($event: any) => (openFile(_ctx.route('v2.salary.payslip.get', { paySlipId: _ctx.payslip.id }))))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(t)('pay-slips.open')), 1)
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                    (canUpdatePayslips.value)
                      ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                          key: 2,
                          onClick: openUploadDialog
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('pay-slips.upload')), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (canUpdatePayslips.value && !isRejected.value && !isApproved.value && hasSomeStatus.value)
                      ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                          key: 3,
                          onClick: openSendReminderDialog
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('pay-slips.reminder-text')), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (isUploaded.value && _unref(hasPermission)(_unref(Permission).SALARY_PAYSLIPS_DELETE))
                      ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                          key: 4,
                          onClick: openDeleteDialog
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('pay-slips.delete')), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (isApprovable.value)
                      ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                          key: 5,
                          onClick: openApproveDialog
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('pay-slips.approve')), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (isApprovable.value)
                      ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                          key: 6,
                          onClick: openRejectDialog
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('pay-slips.reject')), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ], 2))
        : (canUpdatePayslips.value)
          ? (_openBlock(), _createBlock(AddButton, {
              key: 1,
              onClick: openUploadDialog,
              width: "narrow"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('pay-slips.headers.upload')), 1)
              ]),
              _: 1
            }))
          : (_openBlock(), _createElementBlock("span", {
              key: 2,
              class: _normalizeClass(["uppercase", color.value])
            }, _toDisplayString(text.value), 3)),
      (canUpdatePayslips.value)
        ? (_openBlock(), _createBlock(PayslipUpload, {
            key: 3,
            payslip: _ctx.payslip,
            modelValue: uploadModalOpen.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((uploadModalOpen).value = $event))
          }, null, 8, ["payslip", "modelValue"]))
        : _createCommentVNode("", true),
      (canUpdatePayslips.value && !isRejected.value && !isApproved.value)
        ? (_openBlock(), _createBlock(PayslipSendReminder, {
            key: 4,
            payslip: _ctx.payslip,
            modelValue: sendReminderModalOpen.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((sendReminderModalOpen).value = $event)),
            "selected-company-id": user.value.selected_company_id
          }, null, 8, ["payslip", "modelValue", "selected-company-id"]))
        : _createCommentVNode("", true),
      (isUploaded.value && _unref(hasPermission)(_unref(Permission).SALARY_PAYSLIPS_DELETE))
        ? (_openBlock(), _createBlock(PayslipDelete, {
            key: 5,
            payslip: _ctx.payslip,
            modelValue: deleteModalOpen.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((deleteModalOpen).value = $event)),
            "selected-company-id": user.value.selected_company_id
          }, null, 8, ["payslip", "modelValue", "selected-company-id"]))
        : _createCommentVNode("", true),
      (isApprovable.value)
        ? (_openBlock(), _createBlock(PayslipApprove, {
            key: 6,
            payslip: _ctx.payslip,
            modelValue: approveModalOpen.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((approveModalOpen).value = $event)),
            user: user.value
          }, null, 8, ["payslip", "modelValue", "user"]))
        : _createCommentVNode("", true),
      (isApprovable.value)
        ? (_openBlock(), _createBlock(PayslipReject, {
            key: 7,
            payslip: _ctx.payslip,
            modelValue: rejectModalOpen.value,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((rejectModalOpen).value = $event)),
            user: user.value
          }, null, 8, ["payslip", "modelValue", "user"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["data"]))
}
}

})